//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useNavigate } from 'react-router-dom';
import devOpsLogo from '../../icons/loop_logo.png';
import loopGenieIcon from '../../icons/modern/loop_genie_icon.png';
import teamIllustration from '../../icons/no_loops_illustration.png';
import { useLoopContext } from '../../LoopContext';

interface NoLoopsViewProps {
  t: (key: string) => string;
  isAdmin?: boolean;
  failedTeamspace?: boolean;
}

const NoLoopsView: React.FC<NoLoopsViewProps> = ({
  t,
  isAdmin,
  failedTeamspace,
}) => {
  const navigate = useNavigate();
  const { setCreateNewLoop } = useLoopContext();

  return (
    <div
      className="relative h-full overflow-hidden"
      id="id-no-loops-container"
      data-testid="no-loops-container"
    >
      <div id="id-no-loops-wrapper" data-testid="no-loops-wrapper">
        <div
          className="mx-auto max-w-[39rem] pt-20 text-center"
          id="id-no-loops-content"
          data-testid="no-loops-content"
        >
          {/* Centered Logo */}
          <div
            className="mb-4 flex justify-center"
            id="id-no-loops-logo-wrapper"
            data-testid="no-loops-logo-wrapper"
          >
            <img
              src={devOpsLogo}
              alt="DevOps Loop"
              className="h-auto w-full max-w-[200px] object-contain"
            />
          </div>

          {/* Divider */}
          <div
            className="mx-auto mb-4 w-3/4 border-t border-border"
            id="id-no-loops-divider"
            data-testid="no-loops-divider"
          />

          {/* Heading */}
          <h1 className="mb-2 text-2xl font-medium text-foreground">
            {t(isAdmin ? 'no_loops_heading' : 'no_loops_heading_non_admin')}
          </h1>

          {/* Description */}
          <p className="mx-auto mb-6 max-w-[560px] text-sm leading-relaxed text-secondary-foreground">
            {t(
              isAdmin
                ? 'no_loops_description'
                : 'no_loops_description_non_admin',
            )}
          </p>

          {/* Three Instruction Steps — only shown to admins who can create loops */}
          {isAdmin && (
            <div
              className="mb-6 space-y-4 text-left"
              id="id-no-loops-steps-container"
              data-testid="no-loops-steps-container"
            >
              {/* Step 1 */}
              <div
                className="flex items-start gap-3"
                id="id-no-loops-step-1"
                data-testid="no-loops-step-1"
              >
                <div
                  className="flex size-7 shrink-0 items-center justify-center rounded-full border-2 border-primary"
                  id="id-no-loops-step-1-circle"
                  data-testid="no-loops-step-1-circle"
                >
                  <div
                    className="flex size-5 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground"
                    id="id-no-loops-step-1-number"
                    data-testid="no-loops-step-1-number"
                  >
                    1
                  </div>
                </div>
                <div
                  id="id-no-loops-step-1-content"
                  data-testid="no-loops-step-1-content"
                >
                  <h3 className="text-sm font-medium text-foreground">
                    {t('no-loops-step1-title')}
                  </h3>
                  <p className="text-sm text-secondary-foreground">
                    {t('no-loops-step1-description')}
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div
                className="flex items-start gap-3"
                id="id-no-loops-step-2"
                data-testid="no-loops-step-2"
              >
                <div
                  className="flex size-7 shrink-0 items-center justify-center rounded-full border-2 border-primary"
                  id="id-no-loops-step-2-circle"
                  data-testid="no-loops-step-2-circle"
                >
                  <div
                    className="flex size-5 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground"
                    id="id-no-loops-step-2-number"
                    data-testid="no-loops-step-2-number"
                  >
                    2
                  </div>
                </div>
                <div
                  id="id-no-loops-step-2-content"
                  data-testid="no-loops-step-2-content"
                >
                  <div
                    className="flex items-center gap-2"
                    id="id-no-loops-step-2-title-row"
                    data-testid="no-loops-step-2-title-row"
                  >
                    <h3 className="text-sm font-medium text-foreground">
                      {t('no-loops-step2-title')}
                    </h3>
                    <img
                      src={loopGenieIcon}
                      alt=""
                      aria-hidden="true"
                      className="size-5"
                    />
                  </div>
                  <p className="text-sm text-secondary-foreground">
                    {t('no-loops-step2-description')}
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div
                className="flex items-start gap-3"
                id="id-no-loops-step-3"
                data-testid="no-loops-step-3"
              >
                <div
                  className="flex size-7 shrink-0 items-center justify-center rounded-full border-2 border-primary"
                  id="id-no-loops-step-3-circle"
                  data-testid="no-loops-step-3-circle"
                >
                  <div
                    className="flex size-5 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground"
                    id="id-no-loops-step-3-number"
                    data-testid="no-loops-step-3-number"
                  >
                    3
                  </div>
                </div>
                <div
                  id="id-no-loops-step-3-content"
                  data-testid="no-loops-step-3-content"
                >
                  <h3 className="text-sm font-medium text-foreground">
                    {t('no-loops-step3-title')}
                  </h3>
                  <p className="text-sm text-secondary-foreground">
                    {t('no-loops-step3-description')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Create a Loop Button (admin) / Contact admin message (member) */}
          {isAdmin ? (
            <button
              onClick={() => {
                setCreateNewLoop(true);
                navigate('/loop');
              }}
              disabled={failedTeamspace}
              className="rounded-md bg-primary px-6 py-3 font-medium text-primary-foreground transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              id="id-no-loops-create-loop-btn"
              data-testid="id-no-loops-create-loop-btn"
            >
              {t('create-a-loop')}
            </button>
          ) : (
            <p
              className="text-sm text-secondary-foreground"
              id="id-no-loops-contact-admin"
              data-testid="id-no-loops-contact-admin"
            >
              {t('no-loops-contact-admin')}
            </p>
          )}
        </div>
      </div>

      {/* Illustration - Bottom Right */}
      <div
        className="pointer-events-none absolute bottom-0 right-0 w-1/3 max-w-md"
        id="id-no-loops-illustration-wrapper"
        data-testid="no-loops-illustration-wrapper"
      >
        <img
          src={teamIllustration}
          alt="Team collaboration"
          className="h-auto w-full"
        />
      </div>
    </div>
  );
};

export default NoLoopsView;
